import { useQuery } from "@apollo/client";
import { WAVE_PARTICIPANT_HARM_METRICS, WAVE_SESSION_HARM_METRICS } from "../queries/waves";

// Harm metrics are returned as decimal strings by the backend — format for display.
const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "-";
    const sign = n >= 0 ? "" : "-";
    return `${sign}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function ParticipantHarm({ waveId }) {
    const { loading, data, error } = useQuery(WAVE_PARTICIPANT_HARM_METRICS, {
        variables: { waveId }, fetchPolicy: "cache-and-network",
    });
    return (
        <div className="admin-section">
            <h3>Per-participant harm</h3>
            <p style={{ color: "#666", fontSize: 13 }}>
                Each participant's realized outcomes in this wave, grouped by intervention arm.
            </p>
            {loading && <p>Loading…</p>}
            {error && <p style={{ color: "crimson" }}>{error.message}</p>}
            {data && (
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Participant</th><th>Arm</th><th>Trades</th><th>Total P&L</th>
                            <th>Avg / trade</th><th>Largest loss</th><th>Losing trades</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.waveParticipantHarmMetrics.length === 0 && (
                            <tr><td colSpan={7} style={{ color: "#888", textAlign: "center" }}>No trades recorded yet.</td></tr>
                        )}
                        {data.waveParticipantHarmMetrics.map((m) => (
                            <tr key={m.participant_id}>
                                <td>{m.participant_id}</td>
                                <td>{m.arm || "-"}</td>
                                <td>{m.trade_count}</td>
                                <td>{money(m.total_pnl)}</td>
                                <td>{money(m.avg_pnl_per_trade)}</td>
                                <td>{money(m.largest_single_loss)}</td>
                                <td>{m.losing_trade_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function SessionHarm({ waveId }) {
    const { loading, data, error } = useQuery(WAVE_SESSION_HARM_METRICS, {
        variables: { waveId }, fetchPolicy: "cache-and-network",
    });
    return (
        <div className="admin-section">
            <h3>Per-session harm</h3>
            <p style={{ color: "#666", fontSize: 13 }}>
                Aggregate P&L per Phase 3 session and arm, useful for time-series comparison.
            </p>
            {loading && <p>Loading…</p>}
            {error && <p style={{ color: "crimson" }}>{error.message}</p>}
            {data && (
                <table className="admin-table">
                    <thead>
                        <tr><th>Session</th><th>Arm</th><th>Trades</th><th>Total P&L</th><th>Avg P&L</th><th>Losing trades</th></tr>
                    </thead>
                    <tbody>
                        {data.waveSessionHarmMetrics.length === 0 && (
                            <tr><td colSpan={6} style={{ color: "#888", textAlign: "center" }}>No session metrics yet.</td></tr>
                        )}
                        {data.waveSessionHarmMetrics.map((m, i) => (
                            <tr key={`${m.session_id}-${m.arm}-${i}`}>
                                <td>{m.session_id}</td>
                                <td>{m.arm || "-"}</td>
                                <td>{m.trade_count}</td>
                                <td>{money(m.total_pnl)}</td>
                                <td>{money(m.avg_pnl)}</td>
                                <td>{m.losing_trades}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default function HarmMetricsTab({ waveId }) {
    return (
        <div>
            <ParticipantHarm waveId={waveId} />
            <SessionHarm waveId={waveId} />
        </div>
    );
}
